import type { FC } from 'react'
import { useState } from 'react'

import { Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import ImageOffIcon from 'mdi-react/ImageOffIcon'
import MinusIcon from 'mdi-react/MinusIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { ProductGroupData } from '@/modules/ecom-policy/types'
import handleMultipleChoiceItemSelect from '@/util/handleMultipleChoiceItemSelect'

import ProductGroupOverview from './ProductGroupOverview'
import SingleProductGroupOverview from './SingleProductGroupOverview'

const ProductGroup: FC<ProductGroupData> = ({
  groupedProducts,
  imageUrl,
  description,
  priceFrom,
  currency,
  groupName,
  category,
  inStock,
  isMandatory,
}) => {
  const isThreeStepIT = useIsThreeStepIT()
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false)

  const translate = useTranslation()
  const shopCtx = useShopNewOrderContext()
  const searchFilter = shopCtx.searchFilter
  const [selectedProductIds, setSelectedProductIds] = shopCtx.selectedProductIds
  const [productQuantityById, setProductQuantityById] = shopCtx.productQuantityById

  const productIds = groupedProducts.map(({ id }) => id)
  const selectedProductId = selectedProductIds?.find(id => productIds.includes(id))
  const isSelected = Boolean(selectedProductId)
  // Mandatory products are not selectable
  const isSingleProductGroup = groupedProducts.filter(product => !product.isMandatory).length === 1
  const quantity = isSingleProductGroup ? productQuantityById[groupedProducts[0].id] || 0 : 0
  const isSingleProductSelected = Boolean(selectedProductIds?.includes(groupedProducts[0].id))

  // Products marked mandatory should not be visible in product selection
  if (isMandatory) return null

  const isAdmin = useIsAdmin()
  const isQuantitySelectionAllowed =
    isAdmin || (isSingleProductGroup ? groupedProducts[0].quantityLimit > 1 : false)
  const isQuantitySelectionAvailable =
    isSingleProductGroup && isSingleProductSelected && isQuantitySelectionAllowed
  const isQuantityLimitReached =
    isSingleProductGroup && groupedProducts[0]
      ? quantity + 1 > groupedProducts[0].quantityLimit
      : false

  return (
    <button
      className={`inventory__products__product ${isSelected ? 'selected' : 'not-selected'}`}
      onClick={() => setIsProductsModalOpen(true)}
    >
      <div className="img">
        <ImageWithIconFallback
          src={imageUrl ?? ''}
          alt="product"
          fallbackIcon={<ImageOffIcon className="missing-image-placeholder" />}
        />
      </div>

      {isQuantitySelectionAvailable && (
        <div className="absolute right-[11px] top-10 flex flex-col justify-center text-lg">
          <Button
            className={twJoin(
              'm-0 p-0',
              !isThreeStepIT &&
                'text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300'
            )}
            variant="tertiary"
            disabled={isQuantityLimitReached}
            Icon={<PlusIcon className="mr-0" />}
            onClick={e => {
              e.stopPropagation()
              setProductQuantityById({
                ...productQuantityById,
                [groupedProducts[0].id]: quantity + 1,
              })
            }}
          />
          <div className={twJoin(isThreeStepIT ? 'text-threeStepIT' : 'text-green-700')}>
            {quantity}
          </div>
          <Button
            className={twJoin(
              'm-0 p-0',
              !isThreeStepIT &&
                'text-gray-400 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300'
            )}
            variant="tertiary"
            Icon={<MinusIcon className="!mr-0" />}
            onClick={e => {
              e.stopPropagation()
              if (quantity === 1) {
                handleMultipleChoiceItemSelect({
                  selectedId: groupedProducts[0].id,
                  currentIdList: selectedProductIds,
                  setData: setSelectedProductIds,
                })
                return
              }
              setProductQuantityById({
                ...productQuantityById,
                [groupedProducts[0].id]: quantity - 1,
              })
            }}
          />
        </div>
      )}

      <div className="name">
        <Highlight pattern={searchFilter} text={groupName} />
      </div>

      {!isSingleProductGroup && (
        <div className="category">
          <Highlight pattern={searchFilter} text={category} />
        </div>
      )}

      <div className="price">
        {isSingleProductGroup ? (
          <FormattedNumberWithCurrency amount={priceFrom} currency={currency} />
        ) : (
          <>
            {!isSelected && (
              <>
                <Translation id="from" />{' '}
              </>
            )}
            <FormattedNumberWithCurrency amount={priceFrom} currency={currency} />
          </>
        )}
      </div>

      <div className="in-stock">
        {!inStock || inStock === '0' ? (
          <Translation id="Available in stock" />
        ) : (
          <>
            {inStock} <Translation id="in stock" />
          </>
        )}
      </div>

      <div
        className={twJoin(
          'checkmark',
          isSelected ? 'absolute right-2 top-2 block' : 'hidden',
          isSelected && (isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-selected')
        )}
      >
        {isSelected ? (
          <CheckboxMarkedCircleOutlineIcon
            color={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-selected')}
          />
        ) : (
          <CheckboxBlankCircleOutlineIcon />
        )}
      </div>

      <Popup
        variant={isSingleProductGroup ? undefined : 'wide'}
        isOpen={isProductsModalOpen}
        ariaLabel={translate('Modal with products')}
        onClose={() => setIsProductsModalOpen(false)}
      >
        <div>
          {isSingleProductGroup ? (
            <SingleProductGroupOverview
              groupName={groupName}
              currency={currency}
              description={description}
              groupedProducts={groupedProducts}
              isSingleProductSelected={isSingleProductSelected}
              onModalClose={() => setIsProductsModalOpen(false)}
              onProductSelect={() => {
                handleMultipleChoiceItemSelect({
                  selectedId: groupedProducts[0].id,
                  currentIdList: selectedProductIds,
                  setData: setSelectedProductIds,
                })
                setProductQuantityById({
                  ...productQuantityById,
                  [groupedProducts[0].id]: isSingleProductSelected ? 0 : 1,
                })
              }}
            />
          ) : (
            <ProductGroupOverview
              selectedGroupProductId={selectedProductId}
              groupName={groupName}
              groupedProducts={groupedProducts}
              description={description}
              onModalClose={() => setIsProductsModalOpen(false)}
            />
          )}
        </div>
      </Popup>
    </button>
  )
}

export default ProductGroup
